import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Button, Avatar, CircularProgress, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSnackbar } from "notistack";
import { get_school_students_api, resend_student_invite_api } from "../DAL/SchoolPortal/SchoolPortal";
import {
  border, textPrimary, textSecondary, textMuted, accent, accentSoft, font, success,
  secondaryButtonSx,
} from "../layouts/schoolPortal/schoolPortalTheme";

/**
 * Everyone the instructor has added, and where each of them has got to.
 *
 * They add someone, that person appears here as waiting, and the row turns
 * green by itself when the student makes their account. A resend covers the
 * common case of an invite email that went to spam.
 *
 * WHY THIS OWNS ITS OWN TIMER
 * It used to refetch only when a token derived from the parent's counts
 * changed — poll, then counts, then token, then list. Every hop was a chance
 * to be stale, and the parent's poll also stopped as soon as the first student
 * joined, so a second joiner never showed up without a page refresh. The list
 * is what people watch, so it now reads its own source directly for as long as
 * it is on screen, and tells the parent when the joined count moves so the
 * step gate follows it rather than the other way round.
 */
const ROSTER_POLL_MS = 5000;

const initialsOf = (student) =>
  `${(student.first_name || "").charAt(0)}${(student.last_name || "").charAt(0)}`.toUpperCase() || "?";

// Join-link signups arrive already accepted; email invites sit at "pending"
// until the student clicks through. Either flag being set means they are in.
const hasJoined = (student) => Boolean(student.isActivated || student.inviteAcceptedAt);

export default function InstructorStudentRoster({ active = true, refreshToken, onJoinedCountChange }) {
  const { enqueueSnackbar } = useSnackbar();
  const [students, setStudents] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await get_school_students_api();
      if (res.code === 200) setStudents(res.students || []);
      else setStudents([]);
    } catch (e) {
      // Leave whatever is on screen rather than blanking a good list because
      // one poll failed.
      setStudents((prev) => (prev === null ? [] : prev));
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshToken]);

  // Keeps running after the first student joins, unlike the parent's old
  // gate-driven poll — the second and third joiner matter just as much.
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(load, ROSTER_POLL_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  // Sharing a link means leaving the tab. Coming back is exactly when someone
  // wants to know whether it worked, so don't make them wait out the interval.
  useEffect(() => {
    if (!active) return undefined;
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [active, load]);

  // Tell the parent when the number of joined students moves, so the step gate
  // unlocks off the same read that painted the row green.
  const joinedCount = students === null ? null : students.filter(hasJoined).length;
  const lastReported = useRef(null);
  useEffect(() => {
    if (joinedCount === null) return;
    if (lastReported.current !== null && joinedCount !== lastReported.current && onJoinedCountChange) {
      onJoinedCountChange(joinedCount);
    }
    lastReported.current = joinedCount;
  }, [joinedCount, onJoinedCountChange]);

  const handleResend = async (student) => {
    setResendingId(student._id);
    try {
      const res = await resend_student_invite_api(student._id);
      if (res.code === 200) {
        enqueueSnackbar(`Invite resent to ${student.email}`, { variant: "success" });
      } else {
        enqueueSnackbar(res.message || "Could not resend that invite", { variant: "error" });
      }
    } catch (e) {
      enqueueSnackbar("Could not resend that invite", { variant: "error" });
    } finally {
      setResendingId(null);
    }
  };

  if (students === null) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={14} sx={{ color: accent }} />
        <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary }}>
          Loading your students
        </Typography>
      </Box>
    );
  }

  if (students.length === 0) {
    return (
      <Box sx={{
        py: 2, px: 1.75, borderRadius: "10px", border: `1px dashed ${border}`, textAlign: "center",
      }}>
        <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
          Nobody in your group yet
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.25 }}>
          Add your first student below. They'll appear here the moment you do.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {students.map((student, i) => {
        const joined = hasJoined(student);
        const name = `${student.first_name || ""} ${student.last_name || ""}`.trim() || student.email;
        return (
          <Box
            key={student._id}
            sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              py: 1.25, borderTop: i === 0 ? "none" : `1px solid ${border}`,
            }}
          >
            <Avatar sx={{
              width: 32, height: 32, flexShrink: 0, fontSize: "12px", fontWeight: 700,
              bgcolor: joined ? `${success}1f` : accentSoft,
              color: joined ? success : accent,
            }}>
              {initialsOf(student)}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: font, fontSize: "13.5px", fontWeight: 700, color: textPrimary }}>
                {name}
              </Typography>
              <Typography noWrap sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>
                {student.email}
              </Typography>
            </Box>

            {joined ? (
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: "14px !important", color: `${success} !important` }} />}
                label="Joined"
                sx={{
                  height: 24, fontFamily: font, fontSize: "11px", fontWeight: 700,
                  backgroundColor: `${success}14`, color: success,
                }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                <Chip
                  size="small"
                  label="Waiting"
                  sx={{
                    height: 24, fontFamily: font, fontSize: "11px", fontWeight: 700,
                    backgroundColor: "rgba(26,26,26,0.06)", color: textSecondary,
                  }}
                />
                <Button
                  onClick={() => handleResend(student)}
                  disabled={resendingId === student._id}
                  sx={{ ...secondaryButtonSx, minHeight: 28, padding: "3px 10px", fontSize: "11.5px", whiteSpace: "nowrap" }}
                >
                  {resendingId === student._id
                    ? <CircularProgress size={12} sx={{ color: accent }} />
                    : "Resend"}
                </Button>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
